// compare.jsx - High-Density SEO-Optimized Semantic 3-Column Comparison Table.
// One shared React app; the PHP page injects the competitor dataset via
// window.__PK7VM_COMPARE__ before this bundle loads.

const { useState, useEffect, useRef, useCallback } = React;
const { Reveal, Aurora, useScrollRef, useInView } = window;
const { Nav, Footer, DiscordCTA } = window;

// ─── clean SVG icons ────────────────────────────────────────────────────────
function SvgCheck({ size = 14, color = '#10b981' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 3 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SvgCross({ size = 14, color = '#f87171' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 3 }}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SvgInfo({ size = 14, color = 'var(--brand-soft)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 3 }}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function CategoryIcon({ type, size = 18, color = 'var(--brand-soft)' }) {
  if (type === 'shield') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    );
  }
  if (type === 'lock') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    );
  }
  if (type === 'zap') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

// ─── competitor switcher header pill ─────────────────────────────────────────
function CompetitorSwitcher({ current }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(245,241,232,0.04)', border: '1px solid var(--border-strong)', borderRadius: 999, padding: 4, marginBottom: 24, fontFamily: 'var(--font-ui)' }}>
      <a href="pyarmor.php" style={{
        padding: '7px 18px', borderRadius: 999, fontSize: 13, fontWeight: 600,
        color: current === 'PyArmor' ? '#fff' : 'var(--text-dim)',
        background: current === 'PyArmor' ? 'var(--brand)' : 'transparent',
        boxShadow: current === 'PyArmor' ? '0 0 16px rgba(59,130,246,0.4)' : 'none',
        transition: 'all .3s ease', textDecoration: 'none', minHeight: 36, display: 'inline-flex', alignItems: 'center',
      }}>
        vs. PyArmor
      </a>
      <a href="nuitka.php" style={{
        padding: '7px 18px', borderRadius: 999, fontSize: 13, fontWeight: 600,
        color: current === 'Nuitka' ? '#fff' : 'var(--text-dim)',
        background: current === 'Nuitka' ? 'var(--brand)' : 'transparent',
        boxShadow: current === 'Nuitka' ? '0 0 16px rgba(59,130,246,0.4)' : 'none',
        transition: 'all .3s ease', textDecoration: 'none', minHeight: 36, display: 'inline-flex', alignItems: 'center',
      }}>
        vs. Nuitka
      </a>
    </div>
  );
}

// ─── hero ────────────────────────────────────────────────────────────────────
function CompareHero({ competitor, tagline, subtitle }) {
  return (
    <section style={{ position: 'relative', padding: 'clamp(96px, 12vw, 150px) clamp(16px, 4vw, 32px) clamp(40px, 6vw, 60px)', textAlign: 'center', overflow: 'hidden', fontFamily: 'var(--font-ui)' }}>
      <div style={{ position: 'absolute', inset: 0, opacity: 0.35, pointerEvents: 'none' }}>
        <Aurora palette={{ a: '#1e40af', b: '#3b82f6', c: '#0a0a0f' }} />
      </div>
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 860, margin: '0 auto' }}>
        <Reveal>
          <CompetitorSwitcher current={competitor} />
        </Reveal>
        <Reveal delay={60}>
          <h1 style={{ fontSize: 'clamp(34px, 5.5vw, 72px)', fontWeight: 500, letterSpacing: -2, lineHeight: 1.05, color: 'var(--text)', marginBottom: 18, textWrap: 'balance' }}>
            PyK7VM <span style={{ color: 'var(--text-muted)', fontWeight: 300 }}>vs</span> {competitor}
          </h1>
        </Reveal>
        <Reveal delay={120}>
          <p style={{ fontSize: 'clamp(15px, 2vw, 17.5px)', color: 'var(--text-dim)', lineHeight: 1.65, maxWidth: 740, margin: '0 auto' }}>
            {tagline}
          </p>
        </Reveal>
        <Reveal delay={180}>
          <p style={{ marginTop: 14, fontSize: 'clamp(13px, 1.8vw, 14px)', color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 640, margin: '14px auto 0' }}>
            {subtitle}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── executive TL;DR summary card ───────────────────────────────────────────
function TldrStrip({ tldr }) {
  return (
    <section style={{ padding: '0 clamp(16px, 4vw, 32px) clamp(24px, 4vw, 40px)', fontFamily: 'var(--font-ui)' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto', border: '1px solid var(--border-strong)', borderRadius: 16, padding: 'clamp(18px, 3vw, 26px) clamp(16px, 3vw, 32px)', background: 'rgba(245,241,232,0.02)', backdropFilter: 'blur(12px)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: 'var(--brand-soft)', textTransform: 'uppercase', marginBottom: 8, fontFamily: 'var(--font-code)' }}>
          Executive Technical Summary
        </div>
        <p style={{ color: 'var(--text)', fontSize: 'clamp(14px, 1.8vw, 15px)', lineHeight: 1.7, margin: 0 }}>
          {tldr}
        </p>
      </div>
    </section>
  );
}

// ─── semantic 3-column table component ───────────────────────────────────────
function SemanticCategoryTable({ category, competitor }) {
  return (
    <div
      id={`cat-panel-${category.id}`}
      data-cat-id={category.id}
      style={{
        borderRadius: 16,
        border: '1px solid var(--border-strong)',
        background: 'rgba(245,241,232,0.015)',
        backdropFilter: 'blur(12px)',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      }}
    >
      {/* Category Header Strip */}
      <div style={{
        padding: '14px 20px',
        borderBottom: '1px solid var(--border)',
        background: 'rgba(10,10,15,0.7)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CategoryIcon type={category.icon} size={15} />
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: 0, letterSpacing: -0.3 }}>
          {category.name}
        </h3>
      </div>

      {/* Semantic 3-Column Table with Sticky Column Support */}
      <div className="table-responsive-wrapper table-sticky-col">
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13.5, fontFamily: 'var(--font-ui)', minWidth: 620 }}>
          <thead>
            <tr style={{ background: 'rgba(245,241,232,0.03)', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: '12px 18px', width: '34%', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                Capability &amp; Scope
              </th>
              <th style={{ padding: '12px 18px', width: '33%', color: 'var(--text-dim)', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                {competitor}
              </th>
              <th style={{
                padding: '12px 18px', width: '33%',
                background: 'rgba(59,130,246,0.08)',
                borderLeft: '1px solid rgba(59,130,246,0.25)',
                color: 'var(--brand-soft)', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                  <span>PyK7VM</span>
                  <span style={{ fontSize: 9.5, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', padding: '1px 6px', borderRadius: 999 }}>
                    Recommended
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {category.items.map((item, idx) => {
              const isPk7vmWin = item.advantage === 'pk7vm';
              return (
                <tr
                  key={item.feature}
                  style={{
                    borderBottom: idx === category.items.length - 1 ? 'none' : '1px solid var(--border)',
                    transition: 'background .15s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(245,241,232,0.02)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  {/* Feature & Description */}
                  <td style={{ padding: '16px 18px', verticalAlign: 'top' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13.5, marginBottom: 4, letterSpacing: -0.2 }}>
                      {item.feature}
                    </div>
                    {item.desc && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        {item.desc}
                      </div>
                    )}
                  </td>

                  {/* Competitor Cell */}
                  <td style={{ padding: '16px 18px', verticalAlign: 'top', color: 'var(--text-dim)', lineHeight: 1.6, fontSize: 13 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      {isPk7vmWin ? (
                        <SvgCross size={13} color="#f87171" />
                      ) : (
                        <SvgInfo size={13} color="var(--brand-soft)" />
                      )}
                      <span>{item.comp}</span>
                    </div>
                  </td>

                  {/* PyK7VM Highlighted Cell */}
                  <td style={{
                    padding: '16px 18px', verticalAlign: 'top',
                    background: 'rgba(59,130,246,0.035)',
                    borderLeft: '1px solid rgba(59,130,246,0.2)',
                    color: 'var(--text)', lineHeight: 1.6, fontSize: 13,
                  }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <SvgCheck size={13} color="#10b981" />
                      <span>{item.pk7vm}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── split layout: sticky left sidebar + right table stack ───────────────────
function SplitMatrixLayout({ categories, competitor }) {
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id || 'security');

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute('data-cat-id');
          if (id) setActiveCategory(id);
        }
      });
    }, { rootMargin: '-15% 0px -60% 0px' });

    categories.forEach((cat) => {
      const el = document.getElementById(`cat-panel-${cat.id}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [categories]);

  const scrollToCategory = (id) => {
    setActiveCategory(id);
    const el = document.getElementById(`cat-panel-${id}`);
    if (el) {
      const yOffset = -90;
      if (window.__lenis) {
        window.__lenis.scrollTo(el, { offset: yOffset, duration: 0.8 });
      } else {
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  };

  return (
    <section style={{ borderTop: '1px solid var(--border)', padding: 'clamp(50px, 7vw, 70px) clamp(16px, 4vw, 32px)', fontFamily: 'var(--font-ui)' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 44px)', fontWeight: 500, letterSpacing: -1.5, lineHeight: 1.08, color: 'var(--text)', margin: 0 }}>
            Side-by-side technical evaluation.
          </h2>
        </div>

        <style>{`
          .matrix-split-container {
            display: grid;
            grid-template-columns: 240px minmax(0, 1fr);
            gap: 28px;
            align-items: start;
          }
          @media (max-width: 960px) {
            .matrix-split-container {
              grid-template-columns: 1fr !important;
            }
            .matrix-sticky-sidebar {
              position: static !important;
              top: auto !important;
              display: flex !important;
              flex-direction: row !important;
              overflow-x: auto !important;
              padding-bottom: 10px !important;
              margin-bottom: 20px !important;
              -webkit-overflow-scrolling: touch;
            }
            .matrix-sidebar-btn {
              white-space: nowrap !important;
              min-width: 150px !important;
            }
          }
          @media (min-width: 961px) {
            .matrix-split-container {
              grid-template-columns: 240px minmax(0, 1fr) !important;
            }
            .matrix-sticky-sidebar {
              position: sticky !important;
              top: 96px !important;
              display: flex !important;
              flex-direction: column !important;
              overflow-x: visible !important;
              padding-bottom: 0 !important;
              margin-bottom: 0 !important;
            }
            .matrix-sidebar-btn {
              white-space: normal !important;
              min-width: 0 !important;
              width: 100% !important;
            }
          }
        `}</style>

        <div className="matrix-split-container">
          {/* Left Sticky Sidebar */}
          <div className="matrix-sticky-sidebar" style={{
            position: 'sticky', top: 96,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4, paddingLeft: 4 }}>
              Pillars
            </div>
            {categories.map((cat) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => scrollToCategory(cat.id)}
                  className="matrix-sidebar-btn"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                    background: isActive ? 'rgba(59,130,246,0.12)' : 'rgba(245,241,232,0.02)',
                    border: `1px solid ${isActive ? 'rgba(59,130,246,0.4)' : 'var(--border)'}`,
                    color: isActive ? 'var(--text)' : 'var(--text-dim)',
                    textAlign: 'left', fontFamily: 'var(--font-ui)',
                    boxShadow: isActive ? '0 4px 16px rgba(59,130,246,0.12)' : 'none',
                    transition: 'all .2s ease', minHeight: 42,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CategoryIcon type={cat.icon} size={15} color={isActive ? 'var(--brand-soft)' : 'var(--text-muted)'} />
                    <span style={{ fontSize: 12.5, fontWeight: isActive ? 600 : 500 }}>
                      {cat.name}
                    </span>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    padding: '2px 6px', borderRadius: 999,
                    background: isActive ? 'rgba(59,130,246,0.2)' : 'rgba(245,241,232,0.05)',
                    color: isActive ? 'var(--brand-soft)' : 'var(--text-muted)',
                  }}>
                    {cat.items.length}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Right Semantic Tables Stack */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
            {categories.map((cat) => (
              <SemanticCategoryTable
                key={cat.id}
                category={cat}
                competitor={competitor}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── real-world strings dump test visualizer ─────────────────────────────────
const FAKE_PLAINTEXT = [
  'api_key = "sk_live_9F2kX89qLa..."',
  'POST https://api.stripe.com/v1/charges',
  'license_check: status=VALID_ADMIN',
  'def verify_signature(secret, token):',
  'HMAC_SHA256_SEED = "auth_salt_2026"',
  'if user.is_authorized == True:',
  'grant_unrestricted_source_access()',
  'AES_MASTER_KEY = [0x92, 0x1A, 0xC4...]',
];
const FAKE_ENTROPY = [
  '7F 3A 9C 2E 8B 1D 4F 6A 5C 0E 9B 8D',
  'A3 B9 C1 D8 E7 F2 A4 B6 C5 D0 E9 F8',
  'E4 F1 A3 B9 C2 D8 E7 F6 A5 B0 C9 D8',
  'C2 D9 E8 F1 A3 B7 C4 D0 E5 F6 A9 B8',
  'B8 A3 C1 D9 E7 F2 A4 B6 C5 D0 E9 F8',
  'F1 A3 B9 C2 D8 E7 F6 A5 B0 C9 D8 E1',
  'D9 E8 F1 A3 B7 C4 D0 E5 F6 A9 B8 C1',
  '[VIRTUAL MACHINE · NO PLAINTEXT SYMBOLS]',
];

function BinaryDump() {
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.25 });
  const [lines, setLines] = useState({ left: [], right: [] });

  useEffect(() => {
    if (!inView) return;
    let i = 0;
    const id = setInterval(() => {
      if (i >= FAKE_PLAINTEXT.length) { clearInterval(id); return; }
      setLines((s) => ({
        left:  [...s.left,  FAKE_PLAINTEXT[i]],
        right: [...s.right, FAKE_ENTROPY[i]],
      }));
      i++;
    }, 220);
    return () => clearInterval(id);
  }, [inView]);

  const termStyle = {
    background: '#090912', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
    padding: 'clamp(14px, 3vw, 22px)', fontFamily: 'var(--font-code)', fontSize: 'clamp(11px, 2vw, 12px)', lineHeight: 1.75,
    minHeight: 260, overflowX: 'auto', whiteSpace: 'pre', color: '#f1f5f9',
    boxShadow: '0 12px 36px rgba(0,0,0,0.5)', WebkitOverflowScrolling: 'touch',
  };

  return (
    <section style={{ borderTop: '1px solid var(--border)', padding: 'clamp(50px, 7vw, 80px) clamp(16px, 4vw, 32px)', fontFamily: 'var(--font-ui)' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 44px)', fontWeight: 500, letterSpacing: -1.5, lineHeight: 1.08, marginBottom: 12, color: 'var(--text)' }}>
            What <span style={{ fontFamily: 'var(--font-code)', color: 'var(--brand-soft)', background: 'rgba(59,130,246,0.1)', padding: '2px 8px', borderRadius: 6, fontSize: '0.8em' }}>strings</span> reveals in practice.
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14px, 1.8vw, 15.5px)', lineHeight: 1.65, maxWidth: 720, marginBottom: 30 }}>
            The primary reconnaissance step against any protected Python binary is a simple static strings dump.
          </p>
        </Reveal>

        <div ref={ref} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
          <div style={termStyle} className="binary-dump-box stay-dark">
            <div style={{ color: '#f87171', marginBottom: 12, fontSize: 11.5, letterSpacing: 0.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f87171', boxShadow: '0 0 8px #f87171' }} />
              $ strings standard_binary.bin | head -n 8
            </div>
            {lines.left.map((l, i) => (
              <div key={i} style={{ color: '#f87171', opacity: 0.9, animation: 'dump-in .3s ease both' }}>{l}</div>
            ))}
          </div>

          <div style={termStyle} className="binary-dump-box stay-dark">
            <div style={{ color: 'var(--success)', marginBottom: 12, fontSize: 11.5, letterSpacing: 0.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)', boxShadow: '0 0 8px var(--success)' }} />
              $ strings pyk7vm_build.pyd | head -n 8
            </div>
            {lines.right.map((l, i) => (
              <div key={i} style={{ color: i === lines.right.length - 1 ? 'var(--brand-soft)' : 'var(--success)', fontWeight: i === lines.right.length - 1 ? 600 : 400, opacity: 0.85, animation: 'dump-in .3s ease both' }}>{l}</div>
            ))}
          </div>
        </div>
        <style>{`@keyframes dump-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      </div>
    </section>
  );
}

// ─── enterprise verdict ──────────────────────────────────────────────────────
function Verdict({ verdict }) {
  return (
    <section style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative', overflow: 'hidden', textAlign: 'center', fontFamily: 'var(--font-ui)' }}>
      <div style={{ position: 'absolute', inset: 0, opacity: 0.35, pointerEvents: 'none' }}>
        <Aurora palette={{ a: '#1e40af', b: '#3b82f6', c: '#0a0a0f' }} />
      </div>
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 760, margin: '0 auto' }}>
        <Reveal>
          <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 50px)', fontWeight: 500, letterSpacing: -1.8, lineHeight: 1.08, color: 'var(--text)', marginBottom: 16 }}>
            {verdict.headline}
          </h2>
        </Reveal>
        <Reveal delay={80}>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16px)', lineHeight: 1.7, marginBottom: 32 }}>
            {verdict.body}
          </p>
        </Reveal>
        <Reveal delay={140}>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="../register.php" style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              padding: '13px 32px', borderRadius: 999,
              background: 'var(--text)', color: 'var(--bg)', fontSize: 14.5, fontWeight: 600,
              textDecoration: 'none', letterSpacing: -0.1,
              boxShadow: '0 6px 24px rgba(245,241,232,0.15)',
              transition: 'transform .2s', minHeight: 46,
            }}>
              Start Free Trial (Beta)
            </a>
            <a href="../docs.php" style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              padding: '13px 26px', borderRadius: 999,
              background: 'transparent', color: 'var(--text-dim)', fontSize: 14.5, fontWeight: 500,
              textDecoration: 'none', border: '1px solid var(--border-strong)', minHeight: 46,
            }}>
              View Documentation
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── methodology note ───────────────────────────────────────────────────────
function Methodology({ competitor, lastTested }) {
  return (
    <section style={{ borderTop: '1px solid var(--border)', padding: 'clamp(40px, 6vw, 70px) clamp(16px, 4vw, 32px)', fontFamily: 'var(--font-ui)' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <h2 style={{ fontSize: 'clamp(17px, 2.5vw, 20px)', fontWeight: 600, letterSpacing: -0.4, marginBottom: 10, color: 'var(--text)' }}>Methodology &amp; Technical Accuracy Notice</h2>
        <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(13px, 1.8vw, 13.5px)', lineHeight: 1.75 }}>
          This evaluation reflects PyK7VM Engine v9.1 BETA public capabilities and {competitor}'s documented behaviors as of {lastTested} release tracks. Both tools undergo active development. If you observe any discrepancy or outdated metric, please let us know on <a href="https://discord.gg/hpx6XkqmKM" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-soft)', textDecoration: 'underline' }}>Discord</a> and our engineering team will verify and update our records immediately.
        </p>
      </div>
    </section>
  );
}

// ─── sticky floating trial banner ───────────────────────────────────────────
function StickyMatrixBar({ competitor }) {
  const [show, setShow] = useState(false);
  useScrollRef(useCallback((y) => setShow(y > 380), []));

  return (
    <div
      className="sticky-matrix-bar"
      style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
        transform: show ? 'translateY(0)' : 'translateY(110%)',
        transition: 'transform .5s cubic-bezier(.2,.7,.2,1)',
        background: 'rgba(10,10,15,0.94)', backdropFilter: 'blur(20px) saturate(1.3)',
        WebkitBackdropFilter: 'blur(20px) saturate(1.3)',
        borderTop: '1px solid var(--border-strong)',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.6)',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '12px clamp(16px, 4vw, 40px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--brand-soft)', background: 'rgba(59,130,246,0.1)', padding: '3px 10px', borderRadius: 6 }}>
            PyK7VM vs {competitor}
          </span>
          <span style={{ color: 'var(--text-dim)', fontSize: 13, display: 'none' }} className="bar-subtext">
            Zero Bytecode · Polymorphic · Native Speed
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <a href="../register.php" style={{
            background: 'var(--text)', color: 'var(--bg)', padding: '9px 20px',
            borderRadius: 999, fontWeight: 600, fontSize: 13, textDecoration: 'none',
            letterSpacing: -0.1, whiteSpace: 'nowrap',
            boxShadow: '0 0 20px rgba(245,241,232,0.15)',
            transition: 'transform .2s, box-shadow .2s', minHeight: 38, display: 'inline-flex', alignItems: 'center',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 0 26px rgba(245,241,232,0.25)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 0 20px rgba(245,241,232,0.15)'; }}
          >
            Start Free Trial
          </a>
        </div>
      </div>
      <style>{`
        @media(min-width:720px){.bar-subtext{display:inline !important}}
        html.light .sticky-matrix-bar {
          background: rgba(255, 255, 255, 0.94) !important;
          border-top-color: rgba(15, 23, 42, 0.12) !important;
          box-shadow: 0 -8px 32px rgba(15, 23, 42, 0.08) !important;
        }
      `}</style>
    </div>
  );
}

// ─── top-level app ───────────────────────────────────────────────────────────
function CompareApp() {
  const cfg = window.__PK7VM_COMPARE__;

  useEffect(() => {
    if (typeof window.Lenis !== 'undefined') {
      const lenis = new window.Lenis({
        duration: 0.8,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        touchMultiplier: 1.5,
      });
      window.__lenis = lenis;

      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);

      return () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      };
    }
  }, []);

  if (!cfg) {
    return <div style={{ padding: 100, textAlign: 'center', color: 'var(--text-muted)' }}>Comparison dataset missing.</div>;
  }
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <Nav />
      <CompareHero competitor={cfg.competitor} tagline={cfg.tagline} subtitle={cfg.subtitle} />
      <TldrStrip tldr={cfg.tldr} />
      <SplitMatrixLayout categories={cfg.categories} competitor={cfg.competitor} />
      <BinaryDump />
      <Verdict verdict={cfg.verdict} />
      <Methodology competitor={cfg.competitor} lastTested={cfg.lastTested} />
      <StickyMatrixBar competitor={cfg.competitor} />
      <Footer />
      <DiscordCTA />
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(CompareApp));
